import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPinIcon, SunIcon, PlugIcon } from '../ui/Icons';
import { getCityBySlug, getCitiesByState, getFeaturedCities, estimateForCity } from '../../data/cityPricing';
import { getAllServices } from '../../data/services';
import { getAllFaqs } from '../../data/faqs';

function formatPrice(n) {
  return `$${Math.round(n).toLocaleString('en-US')}`;
}

const BILL_SCENARIOS = [100, 150, 200, 300];

function BillScenarioTable({ city }) {
  return (
    <div style={{ overflowX: 'auto', margin: '20px 0' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
        <thead>
          <tr style={{ background: '#f8fafc' }}>
            <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, color: '#374151', borderBottom: '2px solid #e2e8f0' }}>Monthly Bill</th>
            <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, color: '#374151', borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap' }}>System Size</th>
            <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, color: '#374151', borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap' }}>Installed Cost</th>
            <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, color: '#374151', borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap' }}>Payback</th>
          </tr>
        </thead>
        <tbody>
          {BILL_SCENARIOS.map((bill, i) => {
            const est = estimateForCity(city, bill);
            return (
              <tr key={bill} style={{ background: i % 2 === 0 ? 'white' : '#fafafa' }}>
                <td style={{ padding: '10px 14px', color: '#0f172a', fontWeight: 600, borderBottom: '1px solid #f1f5f9' }}>${bill}/mo</td>
                <td style={{ padding: '10px 14px', color: '#475569', borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap' }}>{est.systemSizeKw} kW ({est.panelCount} panels)</td>
                <td style={{ padding: '10px 14px', color: '#1b4d3e', fontWeight: 700, borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap' }}>{formatPrice(est.netCostLow)}–{formatPrice(est.netCostHigh)}</td>
                <td style={{ padding: '10px 14px', color: '#475569', borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap' }}>{est.paybackYears ? `${est.paybackYears} yrs` : '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function TopicLinks() {
  const topics = getAllServices();
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14 }}>
      {topics.map(t => (
        <a key={t.slug} href={`/solar-panels/${t.slug}`} style={{ textDecoration: 'none' }}>
          <div style={{ background: 'white', borderRadius: 10, border: '1px solid #e2e8f0', padding: '16px 18px' }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', lineHeight: 1.4, marginBottom: 6 }}>{t.name}</div>
            <span style={{ fontSize: 12.5, color: '#1b4d3e', fontWeight: 600 }}>See pricing →</span>
          </div>
        </a>
      ))}
    </div>
  );
}

function FaqAccordion({ faqs }) {
  const [openIndex, setOpenIndex] = useState(0);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {faqs.map((faq, i) => {
        const open = openIndex === i;
        return (
          <div key={i} style={{ background: '#fafafa', border: '1px solid #f1f5f9', borderRadius: 10, overflow: 'hidden' }}>
            <button
              onClick={() => setOpenIndex(open ? -1 : i)}
              style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '14px 18px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}
              aria-expanded={open}
            >
              <span style={{ fontWeight: 700, fontSize: 14.5, color: '#0f172a' }}>{faq.q}</span>
              <span style={{ width: 20, height: 20, borderRadius: '50%', background: open ? '#0f172a' : '#e2e8f0', color: open ? '#fff' : '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 14, lineHeight: 1, transition: 'all 0.2s' }}>{open ? '−' : '+'}</span>
            </button>
            {open && (
              <div style={{ padding: '0 18px 16px' }}>
                <p style={{ fontSize: 13.5, color: '#475569', lineHeight: 1.7, margin: 0 }}>{faq.a}</p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function CityPage({ slug }) {
  const city = getCityBySlug(slug);

  if (!city) return (
    <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12 }}>
      <h2 style={{ color: '#0f172a' }}>City not found</h2>
      <a href="/" style={{ color: '#1b4d3e', fontWeight: 600 }}>← Back to home</a>
    </div>
  );

  const otherCitiesInState = getCitiesByState(city.stateSlugRef).filter(c => c.slug !== city.slug);
  const featuredCities = getFeaturedCities().filter(c => c.slug !== city.slug).slice(0, 8);
  const faqs = getAllFaqs().slice(0, 5);
  const est = city.estimate;

  const title = `Solar Panel Cost in ${city.name}, ${city.stateCode} (2026) | Solar Cost Predictor`;
  const description = `See average solar panel costs, savings, and payback period in ${city.name}, ${city.stateName} for 2026, based on real electricity rates and sun hours. Get a free instant estimate.`;

  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.solarcostpredictor.com' },
      { '@type': 'ListItem', position: 2, name: `Solar Cost in ${city.stateName}`, item: `https://www.solarcostpredictor.com/solar-cost/${city.stateSlugRef}` },
      { '@type': 'ListItem', position: 3, name: `Solar Cost in ${city.name}`, item: `https://www.solarcostpredictor.com/solar-cost/city/${city.slug}` },
    ],
  };
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(f => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
  };

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={`https://www.solarcostpredictor.com/solar-cost/city/${city.slug}`} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <script type="application/ld+json">{JSON.stringify(breadcrumb)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div style={{ background: '#f8fafc', minHeight: '100vh', padding: '40px 24px 64px' }}>
        <div style={{ maxWidth: 780, margin: '0 auto' }}>

          <div style={{ display: 'flex', gap: 6, fontSize: 13, color: '#94a3b8', marginBottom: 24, flexWrap: 'wrap' }}>
            <a href="/" style={{ color: '#64748b', textDecoration: 'none' }}>Home</a>
            <span>›</span>
            <a href={`/solar-cost/${city.stateSlugRef}`} style={{ color: '#64748b', textDecoration: 'none' }}>{city.stateName}</a>
            <span>›</span>
            <span style={{ color: '#0f172a' }}>{city.name}</span>
          </div>

          <div style={{ background: 'white', borderRadius: 14, border: '1px solid #e2e8f0', padding: '32px 36px', marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <MapPinIcon size={18} color="#1b4d3e" />
              <span style={{ fontSize: 12, fontWeight: 700, color: '#1b4d3e', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{city.name}, {city.stateCode}</span>
            </div>
            <h1 style={{ fontSize: 'clamp(24px,4vw,32px)', fontWeight: 800, color: '#0f172a', lineHeight: 1.25, marginBottom: 10 }}>Solar Panel Cost in {city.name}, {city.stateCode} (2026)</h1>
            <p style={{ fontSize: 15.5, color: '#64748b', lineHeight: 1.7, marginBottom: 20 }}>
              {city.name} homeowners pay a flat $2.80/watt installed nationwide — what's different here is {city.stateName}'s electricity rate ({(city.electricityRate * 100).toFixed(1)}¢/kWh) and sun hours ({city.sunHours} peak hours/day), which change how big a system you need to offset a typical bill.
            </p>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 30, fontWeight: 800, color: '#1b4d3e' }}>{formatPrice(est.netCostLow)} – {formatPrice(est.netCostHigh)}</span>
              <span style={{ fontSize: 13, color: '#94a3b8' }}>installed cost, $150/mo bill</span>
            </div>
            <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 6 }}>≈ {est.systemSizeKw} kW system ({est.panelCount} panels) · {est.paybackYears ? `${est.paybackYears}-year payback` : ''}</div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginTop: 20 }}>
              <div style={{ background: '#f8fafc', borderRadius: 10, padding: '12px 14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}><PlugIcon size={11} />Elec. Rate</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>{(city.electricityRate * 100).toFixed(1)}¢/kWh</div>
              </div>
              <div style={{ background: '#f8fafc', borderRadius: 10, padding: '12px 14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}><SunIcon size={11} />Sun Hours</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>{city.sunHours}/day</div>
              </div>
              <div style={{ background: '#f8fafc', borderRadius: 10, padding: '12px 14px' }}>
                <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Annual Savings</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>{formatPrice(est.annualSavings)}</div>
              </div>
              <div style={{ background: '#f8fafc', borderRadius: 10, padding: '12px 14px' }}>
                <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Bill Offset</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>{est.offsetPercent}%</div>
              </div>
            </div>
          </div>

          <div style={{ background: 'linear-gradient(135deg, #1b4d3e, #16324f)', borderRadius: 12, padding: '18px 24px', marginBottom: 28, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <div style={{ color: 'white' }}>
              <div style={{ fontWeight: 700, fontSize: 15 }}>Get a personalized estimate for {city.name}</div>
              <div style={{ fontSize: 13, opacity: 0.9 }}>Free · No signup · 2 minutes</div>
            </div>
            <a href="/" style={{ background: 'white', color: '#1b4d3e', padding: '10px 20px', borderRadius: 8, textDecoration: 'none', fontWeight: 700, fontSize: 14, whiteSpace: 'nowrap' }}>
              Calculate Now →
            </a>
          </div>

          <div style={{ background: 'white', borderRadius: 14, border: '1px solid #e2e8f0', padding: '32px 36px', marginBottom: 24 }}>
            <h2 style={{ fontSize: 19, fontWeight: 800, color: '#0f172a', marginBottom: 6 }}>Cost by Monthly Bill in {city.name}</h2>
            <p style={{ fontSize: 13.5, color: '#64748b', marginBottom: 4 }}>System size and cost scale with your usage — here's what different bill sizes look like in {city.name}.</p>
            <BillScenarioTable city={city} />

            <h2 style={{ fontSize: 19, fontWeight: 800, color: '#0f172a', marginTop: 32, marginBottom: 14 }}>Solar Cost Topics</h2>
            <TopicLinks />

            <h2 style={{ fontSize: 19, fontWeight: 800, color: '#0f172a', marginTop: 32, marginBottom: 14 }}>FAQs</h2>
            <FaqAccordion faqs={faqs} />
          </div>

          <div style={{ background: '#eaf3ee', border: '1px solid #c7d2e0', borderRadius: 12, padding: '24px 28px', marginBottom: 32, textAlign: 'center' }}>
            <div style={{ fontWeight: 800, fontSize: 18, color: '#0f172a', marginBottom: 6 }}>Ready to get an accurate estimate?</div>
            <p style={{ fontSize: 14, color: '#64748b', marginBottom: 16 }}>Use our free calculator for a personalized solar estimate in {city.name} in under 2 minutes.</p>
            <a href="/" style={{ background: '#1b4d3e', color: 'white', padding: '12px 28px', borderRadius: 9, textDecoration: 'none', fontWeight: 700, fontSize: 15 }}>
              Get My Free Estimate →
            </a>
          </div>

          {otherCitiesInState.length > 0 && (
            <div style={{ marginBottom: 32 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 16 }}>More Cities in {city.stateName}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {otherCitiesInState.map(c => (
                  <a key={c.slug} href={`/solar-cost/city/${c.slug}`} style={{ fontSize: 12.5, color: '#64748b', textDecoration: 'none', background: 'white', border: '1px solid #e2e8f0', borderRadius: 20, padding: '6px 12px' }}>
                    {c.name}
                  </a>
                ))}
                <a href={`/solar-cost/${city.stateSlugRef}`} style={{ fontSize: 12.5, color: '#1b4d3e', fontWeight: 700, textDecoration: 'none', background: '#eaf3ee', border: '1px solid #c7d2e0', borderRadius: 20, padding: '6px 12px' }}>
                  All of {city.stateName} →
                </a>
              </div>
            </div>
          )}

          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 16 }}>Popular Cities</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {featuredCities.map(c => (
                <a key={c.slug} href={`/solar-cost/city/${c.slug}`} style={{ fontSize: 12.5, color: '#64748b', textDecoration: 'none', background: 'white', border: '1px solid #e2e8f0', borderRadius: 20, padding: '6px 12px' }}>
                  {c.name}, {c.stateCode}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
